import { useState } from "react";
import { useWalletBalance, useWalletTransactions, useWalletPayments, useProfile } from "../hooks/useApi";
import Card from "../components/Card";
import Button from "../components/Button";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import DepositModal from "../components/DepositModal";
import InvoiceDrawer from "../components/InvoiceDrawer";
import GlassModal from "../components/GlassModal";
import InlinePhoneVerification from "../components/InlinePhoneVerification";
import { formatToman, formatTomanInt, formatNumber } from "../utils/format";

export default function Wallet() {
  const { data: wallet, isLoading: balanceLoading } = useWalletBalance();
  const { data: transactions, isLoading: txLoading } = useWalletTransactions();
  const { data: payments, isLoading: paymentsLoading } = useWalletPayments();
  const { data: user } = useProfile();

  const [showDeposit, setShowDeposit] = useState(false);
  const [showPhoneRequired, setShowPhoneRequired] = useState(false);
  const [showDebtHint, setShowDebtHint] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Record<string, unknown> | null>(null);

  const isPhoneVerified = !!user?.phone && !!user?.phone_verified;

  const handleDepositClick = () => {
    if (user && !isPhoneVerified) {
      setShowPhoneRequired(true);
      return;
    }
    setShowDeposit(true);
  };

  const handlePhoneVerified = () => {
    setShowPhoneRequired(false);
    // give query invalidation a moment then open deposit
    setTimeout(() => setShowDeposit(true), 300);
  };

  const txColumns = [
    {
      key: "type",
      header: "نوع",
      render: (item: Record<string, unknown>) => {
        switch (item.type) {
          case "deposit": return <Badge variant="success">واریز</Badge>;
          case "api_usage": return <Badge variant="error">مصرف API</Badge>;
          case "refund": return <Badge variant="warning">بازگشت</Badge>;
          case "subscription": return <Badge variant="info">اشتراک</Badge>;
          default: return <Badge variant="info">{item.type as string}</Badge>;
        }
      },
    },
    {
      key: "amount",
      header: "مبلغ (تومان)",
      render: (item: Record<string, unknown>) => {
        const amt = Number(item.amount);
        const isPositive = amt > 0;
        return (
          <span
            dir="ltr"
            className={`font-number ltr inline-block ${isPositive ? "text-primary" : "text-text-main"}`}
          >
            {isPositive ? "+" : ""}{formatToman(amt)}
          </span>
        );
      },
    },
    {
      key: "balance_after",
      header: "موجودی پس از (تومان)",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary ltr text-left block">
          {formatToman(item.balance_after as string | number | null | undefined)}
        </span>
      ),
    },
    {
      key: "description",
      header: "توضیحات",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-secondary">{item.description as string || "-"}</span>
      ),
    },
    {
      key: "created_at",
      header: "تاریخ",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-tertiary">{new Date(item.created_at as string).toLocaleDateString("fa-IR")}</span>
      ),
    },
  ];

  const paymentColumns = [
    {
      key: "id",
      header: "کد",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {String(item.id)}
        </span>
      ),
    },
    {
      key: "amount",
      header: "مبلغ (تومان)",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-text-main">
          {formatToman(item.amount as string | number | null | undefined)}
        </span>
      ),
    },
    {
      key: "status",
      header: "وضعیت",
      render: (item: Record<string, unknown>) => {
        switch (item.status) {
          case "verified": return <Badge variant="success">تأیید شده</Badge>;
          case "pending": return <Badge variant="warning">در انتظار</Badge>;
          case "failed": return <Badge variant="error">ناموفق</Badge>;
          case "expired": return <Badge variant="warning">منقضی شده</Badge>;
          default: return <Badge variant="info">{item.status as string}</Badge>;
        }
      },
    },
    {
      key: "ref_id",
      header: "کد رهگیری",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {item.ref_id ? String(item.ref_id) : "—"}
        </span>
      ),
    },
    {
      key: "status_code",
      header: "کد وضعیت",
      render: (item: Record<string, unknown>) => (
        <span className="font-number text-small text-text-tertiary" dir="ltr">
          {(item.status_code as string) || "—"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "تاریخ",
      render: (item: Record<string, unknown>) => (
        <span className="text-small text-text-tertiary">
          {new Date(item.created_at as string).toLocaleDateString("fa-IR")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "عملیات",
      render: (item: Record<string, unknown>) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPayment(item);
          }}
          className="text-small text-primary hover:underline transition-colors"
        >
          {item.status === "verified" ? "مشاهده فاکتور" : "مشاهده جزییات"}
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-h1 text-text-main">کیف پول</h1>
        <p className="text-body text-text-secondary mt-1">مدیریت موجودی و تراکنش‌ها</p>
      </div>

      {/* Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="md:col-span-2 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="relative">
            <p className="text-small text-text-tertiary mb-1">موجودی کیف پول</p>
            {balanceLoading ? (
              <div className="h-9 w-40 bg-surface-hover rounded animate-pulse" />
            ) : (
              <p className="text-[32px] font-semibold text-text-main font-number text-left">
                {formatTomanInt(wallet?.balance)}
                <span className="text-base text-text-tertiary font-normal mr-2">تومان</span>
              </p>
            )}
            <p className="text-small text-text-tertiary mt-1">
              معادل {formatNumber(wallet?.balance)} ریال
            </p>
            <Button onClick={handleDepositClick} size="lg" className="mt-5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              افزایش موجودی
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <p className="text-small text-text-tertiary">بدهی معوق</p>
            <button
              type="button"
              onClick={() => setShowDebtHint((v) => !v)}
              aria-expanded={showDebtHint}
              aria-label="توضیح بدهی معوق"
              title="توضیحات"
              className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${showDebtHint ? "text-primary bg-primary-light" : "text-text-tertiary hover:text-primary hover:bg-primary-light"}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          </div>
          {showDebtHint && (
            <p className="text-[11px] leading-5 text-text-secondary bg-surface-hover border border-line rounded-btn p-3 mb-3">
              به دلیل نهایی شدن هزینه درخواست‌ها پس از پاسخ تامین‌کننده، و برای جلوگیری از سوخت شدن پاسخ تامین‌کننده به دلیل کمبود موجودی کیف پول، پلتفرم تا میزان اندکی بدهی را در نظر می‌گیرد تا ارسال درخواست به کاربران متوقف نشود. پس از شارژ کیف پول، این بدهی به‌صورت خودکار از موجودی کیف پول کسر خواهد شد.
            </p>
          )}
          {balanceLoading ? (
            <div className="h-9 w-32 bg-surface-hover rounded animate-pulse" />
          ) : wallet && Number(wallet.debt) > 0 ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-status-error-bg flex items-center justify-center">
                  <span className="text-status-error-text font-bold text-h3">!</span>
                </div>
                <div>
                  <p className="text-[20px] font-semibold text-status-error-text font-number text-left">
                    {formatTomanInt(wallet.debt)}
                    <span className="text-sm text-text-tertiary font-normal mr-1">تومان</span>
                  </p>
                  <p className="text-small text-text-tertiary">مبلغ قابل تسویه از مصرف API</p>
                </div>
              </div>
              <hr className="my-4 border-line" />
              <p className="text-small text-text-tertiary">
                با اولین افزایش موجودی، این مبلغ به‌صورت خودکار از کیف پول کسر می‌شود.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-status-success-bg flex items-center justify-center">
                <span className="text-status-success-text font-bold text-h3">✓</span>
              </div>
              <div>
                <p className="text-body font-medium text-text-main">بدون بدهی</p>
                <p className="text-small text-text-tertiary">هیچ مبلغ معوقی ندارید</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Payment History */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h2 text-text-main">تاریخچه پرداخت‌ها</h2>
        </div>
        <DataTable
          columns={paymentColumns}
          data={(payments || []) as Record<string, unknown>[]}
          loading={paymentsLoading}
          emptyMessage="هیچ پرداختی ثبت نشده است"
        />
      </Card>

      {/* Transaction History */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h2 text-text-main">تاریخچه تراکنش‌های کیف پول</h2>
        </div>
        <DataTable
          columns={txColumns}
          data={(transactions || []) as Record<string, unknown>[]}
          loading={txLoading}
          emptyMessage="هیچ تراکنشی ثبت نشده است"
        />
      </Card>

      {/* Deposit Modal */}
      <DepositModal open={showDeposit} onClose={() => setShowDeposit(false)} />

      {/* Phone verification required — inline verification */}
      <GlassModal
        open={showPhoneRequired}
        onClose={() => setShowPhoneRequired(false)}
        title="تأیید شماره موبایل"
      >
        <InlinePhoneVerification
          initialPhone={user?.phone || ""}
          onVerified={handlePhoneVerified}
          onCancel={() => setShowPhoneRequired(false)}
        />
      </GlassModal>

      {/* Invoice Drawer */}
      <InvoiceDrawer selectedPayment={selectedPayment} onClose={() => setSelectedPayment(null)} />
    </div>
  );
}